import {
  ETIQUETA_CAMPO_EXPORTACION,
  ETIQUETA_CAMPO_EXPORTACION_EN,
  type CampoEditable,
  type CatalogosDeSeguimiento,
  type ResultadoDelLote,
} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useLoteDeCambios} from '../api/seguimiento';
import {et, tr} from '../i18n/idioma';
import {CAMPO_POR_CLAVE, CAMPOS_EDITABLES, convertirValor} from './campos-exportacion';
import {EntradaDeCampo} from './entrada-de-campo';
import {ResultadoDeLote} from './resultado-de-lote';

type Props = {
  codigos: string[];
  catalogos: CatalogosDeSeguimiento | undefined;
  onCerrar: () => void;
  onAplicado: () => void;
};

type Linea = {id: number; campo: CampoEditable | ''; valor: string};

// Cambio en lote: el mismo valor en uno o varios campos para todas las
// exportaciones seleccionadas. Primero se revisa que cambiaria y despues se aplica.
export function CambioEnLote({codigos, catalogos, onCerrar, onAplicado}: Props) {
  const [lineas, setLineas] = useState<Linea[]>([{id: 1, campo: '', valor: ''}]);
  const [resultado, setResultado] = useState<ResultadoDelLote | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const lote = useLoteDeCambios();
  const disponibles = CAMPOS_EDITABLES.filter(c => c.clave !== 'codigo');

  const cambiosArmados = (): Record<string, unknown> | null => {
    const cambios: Record<string, unknown> = {};
    for (const linea of lineas) {
      if (!linea.campo) continue;
      const convertido = convertirValor(linea.campo, linea.valor);
      if ('error' in convertido) {
        setMensaje(convertido.error);
        return null;
      }
      cambios[linea.campo] = convertido.valor;
    }
    if (!Object.keys(cambios).length) {
      setMensaje(tr('Elige al menos un campo y su valor nuevo.', 'Choose at least one field and its new value.'));
      return null;
    }
    return cambios;
  };

  // Huellas de lo revisado: al aplicar los mismos cambios, las exportaciones
  // que otra persona cambio despues de la revision se omiten y se avisa.
  const [revisado, setRevisado] = useState<{cambios: string; huellas: Map<string, string>} | null>(null);

  const ejecutar = async (modo: 'previsualizar' | 'aplicar') => {
    setMensaje(null);
    const cambios = cambiosArmados();
    if (!cambios) return;
    const clave = JSON.stringify(cambios);
    const huellas = modo === 'aplicar' && revisado?.cambios === clave ? revisado.huellas : null;
    try {
      const r = await lote.mutateAsync({
        modo,
        origen: 'lote',
        omitirConErrores: modo === 'aplicar',
        crearSiNoExiste: false,
        elementos: codigos.map(codigo => {
          const huella = huellas?.get(codigo);
          return huella ? {codigo, cambios, huella} : {codigo, cambios};
        }),
      });
      setResultado(r);
      setRevisado(
        modo === 'previsualizar'
          ? {cambios: clave, huellas: new Map(r.elementos.flatMap(x => (x.huella ? [[x.codigo, x.huella]] : [])))}
          : null,
      );
      if (modo === 'aplicar') onAplicado();
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi ? error.message : tr('No se pudo hacer el cambio.', 'Could not make the change.'),
      );
    }
  };

  const cambiarLinea = (id: number, cambio: Partial<Linea>) =>
    setLineas(actual => actual.map(l => (l.id === id ? {...l, ...cambio} : l)));

  return (
    <section
      aria-label={tr('Cambio en lote', 'Batch change')}
      className="rounded-lg border border-verde/50 bg-white p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-medium text-verde-oscuro">
            {tr(`Cambiar en lote ${codigos.length} exportaciones`, `Batch change ${codigos.length} exports`)}
          </h2>
          <p className="text-xs text-apagado">
            {tr(
              'El mismo valor para todas las seleccionadas. Deja el valor vacío para borrar el campo. Cada exportación deja su cambio en el historial.',
              'The same value for all selected exports. Leave the value empty to clear the field. Each export records its change in the history.',
            )}
          </p>
          <p className="mt-1 max-h-12 overflow-auto text-xs text-apagado">{codigos.join(', ')}</p>
        </div>
        <button type="button" onClick={onCerrar} className="text-sm text-apagado hover:text-tinta">
          {tr('✕ Cerrar', '✕ Close')}
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {lineas.map(linea => {
          const definicion = linea.campo ? CAMPO_POR_CLAVE.get(linea.campo) : undefined;
          return (
            <div key={linea.id} className="grid items-end gap-2 md:grid-cols-[1fr_1fr_auto]">
              <label className="block text-xs text-apagado">
                {tr('Campo', 'Field')}
                <select
                  value={linea.campo}
                  onChange={e => cambiarLinea(linea.id, {campo: e.target.value as CampoEditable | '', valor: ''})}
                  aria-label={tr('Campo', 'Field')}
                  className="mt-1 w-full rounded-md border border-linea px-2 py-1.5 text-sm text-tinta"
                >
                  <option value="">{tr('Elige un campo', 'Choose a field')}</option>
                  {disponibles.map(c => (
                    <option key={c.clave} value={c.clave}>
                      {et(ETIQUETA_CAMPO_EXPORTACION, ETIQUETA_CAMPO_EXPORTACION_EN)[c.clave]}
                    </option>
                  ))}
                </select>
              </label>
              <div>
                {definicion ? (
                  <label className="block text-xs text-apagado">
                    {tr('Valor nuevo', 'New value')}
                    <EntradaDeCampo
                      campo={definicion}
                      valor={linea.valor}
                      catalogos={catalogos}
                      onCambiar={v => cambiarLinea(linea.id, {valor: v})}
                      compacta
                    />
                  </label>
                ) : (
                  <span className="block text-xs text-apagado">{tr('Valor nuevo', 'New value')}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setLineas(actual => actual.filter(l => l.id !== linea.id))}
                disabled={lineas.length === 1}
                className="rounded-md border border-linea bg-white px-2 py-1.5 text-sm text-apagado hover:bg-papel disabled:opacity-40"
                aria-label={tr('Quitar este campo', 'Remove this field')}
              >
                ✕
              </button>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => setLineas(actual => [...actual, {id: Date.now(), campo: '', valor: ''}])}
          className="text-sm text-verde hover:underline"
        >
          {tr('＋ Otro campo', '＋ Another field')}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void ejecutar('previsualizar')}
          disabled={lote.isPending}
          className="rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro disabled:opacity-50"
        >
          {tr('Revisar qué cambiaría', 'Review what would change')}
        </button>
        <button
          type="button"
          onClick={() => void ejecutar('aplicar')}
          disabled={lote.isPending || !resultado || resultado.modo === 'aplicar'}
          className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          title={!resultado ? tr('Primero revisa qué cambiaría', 'First review what would change') : ''}
        >
          {lote.isPending ? tr('Trabajando…', 'Working…') : tr('Aplicar a todas', 'Apply to all')}
        </button>
        {mensaje && <span className="text-sm text-alerta">{mensaje}</span>}
      </div>

      {resultado && <ResultadoDeLote resultado={resultado} className="mt-3" />}
    </section>
  );
}
